import { execAdmin } from '@src/apis/reware/utils/exec';

import { parseDeliveryAddress, parseImageUrls } from './types';
import type {
  FetchOrdersFilters,
  OrderConstants,
  OrdersPage,
  OrdersResult,
  RawOrder,
} from './types';

// Cloudinary resize args for line-item thumbnails (same convention as reware-admin)
const THUMBNAIL = { height: 300, width: 300, quality: 80, crop: 'fit' };

const STATE_TRANSITIONS = `
  stateTransitions {
    id
    createdAt
    changeType
    changeFrom
    changeTo
    reason
    note
    changeDetails
    user { name }
  }
`;

interface FetchOrdersResponse {
  orders: OrdersPage;
}

/**
 * Partner-scoped version of the admin `orders` query used by the reware-admin
 * Orders portal, extended with the per-order detail selection so the expanded
 * row has full field parity. Filters map onto OrderFiltersInputTypeAdmin.
 */
export const fetchOrders = async (filters: FetchOrdersFilters): Promise<OrdersResult> => {
  const query = `
    query fetchOrders(
      $filters: OrderFiltersInputTypeAdmin
      $height: Int
      $width: Int
      $quality: Int
      $crop: String
    ) {
      orders(filters: $filters) {
        count
        page
        pageSize
        rows {
          id
          uuid
          state
          deliveryAddress
          orderPlacedAt
          orderCapturedAt
          returnableUntil
          shippingLevel
          total
          amount
          catalogAmount
          giftCardTotal
          sales_tax
          shipping
          shippingBase
          shippingSurcharge
          taxesIncluded
          taxLines { title price rate }
          transactionCurrencyCode
          externalOrderNumber
          commercePlatform
          paymentVendor
          paymentId
          paymentUrl
          isOrderLocked
          is3plOrderStalled
          is3plOrderNotificationSent
          orderRecentWebhookEventError
          salesAssociate
          saleWarehouse { typeDisplay purpose code name }
          canBeCanceled
          canBeDiscounted
          canBeReturned
          canBeShortShipped
          canExtendReturnability
          canIssueGiftcard
          canHaveFreeShipping
          canMarkAllMissingItems
          paymentDetails {
            originalTotal refundedTotal remainingTotal
            originalCharge refundedCharge remainingCharge
            originalGiftCardAmount refundedGiftCardAmount remainingGiftCardAmount csGiftCardAmount
            # lastFour, never number. The full gift card number is sensitive: the Gift cards page
            # masks it to ***<last3> on list queries and the only sanctioned way to see it whole
            # is giftcard.sensitiveDetails, which is partner-scoped (requirePartner: true) and
            # writes a 'revealed' audit record. This path has none of that — PaymentDetails fills
            # these lists with plain objects straight off the model, so number comes back in the
            # clear with no gate, no masking and no audit entry. Selecting it would put every
            # tendered card number for every row of every page into the browser.
            paymentGiftCards { lastFour amount }
            refundedGiftCards { lastFour amount }
            csGiftCards { lastFour amount }
            ccLast4 ccBrand fundingSource
            salesTaxAmt shippingAmt productAmt discountAmt totalAmt
          }
          promotionDiscounts { code amount name }
          ypackages {
            id state stateTransitionAt carrier carrierService
            trackingNumber trackingUrl length width height weight
            actualShippingCost isResidential isYpackageLocked canBeShortShipped
            location { id code }
          }
          returnYpackages {
            id state stateTransitionAt carrier carrierService
            trackingNumber trackingUrl length width height weight
            actualShippingCost inboundCost isResidential
            location { id code }
          }
          ${STATE_TRANSITIONS}
          escalations {
            id triggerTypeDisplay shipmentTypeDisplay reasonDisplay
            closedReason closedAt createdAt deletedAt warehouseId userId
          }
          orderEmailHistory {
            id displayName subjectLine emailType toEmail senderName date state txUrl
          }
          orderItems {
            id
            saleDisplayPrice
            sale_price
            sale_condition
            deployedCondition
            sale_surcharge_amount
            taxAmount
            refundValue
            saleNotes
            addedAt
            removedAt
            returnedAt
            returnInitiatedAt
            returnReason
            canBeReturned
            canBeRemoved
            canBeDiscounted
            canBeFlagged
            canClearProblem
            ypackageId
            externalReturnId
            discounts { id state style amount percent createdAt }
            catalogItem {
              brand title price size color display_color sku partnerSku parent_sku upc season_display
            }
            inventoryItem {
              id
              uuid
              state
              price
              condition
              deployedCondition
              troveSerialNumber
              sourcePartnerId
              imageUrls(height: $height, width: $width, quality: $quality, crop: $crop)
              location { id code }
            }
            refund { id state createdAt flaggedReason warehouse { code } }
          }
        }
      }
    }
  `;

  const data = await execAdmin<
    { filters: FetchOrdersFilters } & typeof THUMBNAIL,
    FetchOrdersResponse
  >(query, { variables: { filters, ...THUMBNAIL } });

  // Every field on PagedOrderAdmin is nullable, and a broken response otherwise renders
  // identically to a legitimately empty one: `rows: null` throws inside the data provider, and
  // `count: null` becomes a silent `total: undefined` that breaks pagination without an error.
  const page = data?.orders;
  if (!page || !Array.isArray(page.rows)) {
    throw new Error('Order search returned no result set');
  }
  // A null count is as malformed as null rows, and tolerating it is not the safe option it looks
  // like: `total: 0` makes react-admin render the rows it has while offering no page 2, so the
  // tail of the result set is silently unreachable — the same failure the always-send pageSize
  // rule exists to prevent.
  if (typeof page.count !== 'number') {
    throw new Error('Order search returned no result count');
  }

  // The schema makes rows a nullable list of nullable elements, so this narrowing is what lets
  // every caller treat the result as a plain array. Without it a single null row reaches
  // OrderList's `order.uuid.split('-')` and takes down the whole datagrid. Report what it drops:
  // silently returning 23 of 25 rows under a "1–25 of 143" pager means an operator working an
  // order queue never learns those records existed.
  const readable = page.rows.filter((row): row is RawOrder => row != null);
  if (readable.length !== page.rows.length) {
    console.error('Order page dropped unreadable rows', {
      dropped: page.rows.length - readable.length,
      received: page.rows.length,
    });
  }

  // Both JSON scalars are parsed at the boundary, not asserted. Every consumer then reads a value
  // that has actually been checked rather than one the record type merely claims — and a shape
  // change surfaces as one console error here instead of as em dashes spread across the Customer
  // column, or an <img src="h"> where a placeholder belongs.
  const rows = readable.map(row => ({
    ...row,
    deliveryAddress: parseDeliveryAddress(row.deliveryAddress),
    orderItems: row.orderItems?.map(item => (item == null ? null : {
      ...item,
      inventoryItem: item.inventoryItem == null ? item.inventoryItem : {
        ...item.inventoryItem,
        imageUrls: parseImageUrls(item.inventoryItem.imageUrls),
      },
    })),
  }));

  return { rows, count: page.count };
};

interface FetchOrderConstantsResponse {
  application: {
    constants: {
      order: {
        states: OrderConstants['states'];
        orderRefundDialogReasons: OrderConstants['refundReasons'];
        manualGiftcardReasons: OrderConstants['giftcardReasons'];
      };
      ypackage: { states: OrderConstants['ypackageStates'] };
      inventoryItem: { allConditions: OrderConstants['conditions'] };
      return: { problemReturnReasons: OrderConstants['returnReasons'] };
      freeShipping: { reasons: OrderConstants['freeShippingReasons'] };
    };
  };
}

/** Order / package states, conditions, and refund / gift-card reasons. */
export const fetchOrderConstants = async (): Promise<OrderConstants> => {
  const query = `
    query fetchOrderConstants {
      application {
        constants {
          order {
            states { code displayName }
            orderRefundDialogReasons { code displayName }
            manualGiftcardReasons { code displayName }
          }
          ypackage {
            states { code displayName }
          }
          inventoryItem {
            allConditions { code displayName }
          }
          # problemReturnReasons, not returnReasons: this page always initiates returns with
          # isFreeReturnShipping, which is the problem-order flow.
          return {
            problemReturnReasons { code displayName }
          }
          freeShipping {
            reasons { code displayName }
          }
        }
      }
    }
  `;

  const data = await execAdmin<Record<string, never>, FetchOrderConstantsResponse>(
    query,
    { variables: {} }
  );

  const { constants } = data.application;
  return {
    states: constants.order.states,
    ypackageStates: constants.ypackage.states,
    conditions: constants.inventoryItem.allConditions,
    refundReasons: constants.order.orderRefundDialogReasons,
    giftcardReasons: constants.order.manualGiftcardReasons,
    returnReasons: constants.return.problemReturnReasons,
    freeShippingReasons: constants.freeShipping.reasons,
  };
};
